import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { RideStatusType } from '../../../shared/enums/rideStatusType';
import { RideBoardListItem } from '../../../shared/interfaces/ride-board-list-item';
import { RideDocumentDto, RideExtraFieldDocumentDto } from '../../../shared/interfaces/ride-firestore';
import { FireStoreService } from '../../../shared/services/firestore.service';
import { StateService } from '../../../shared/services/state.service';
import { orderBy, QueryConstraint, where } from 'firebase/firestore';
import moment from 'moment';
import { MessageService, TooltipOptions } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ScrollerModule } from 'primeng/scroller';
import { debounceTime, fromEvent, Subscription } from 'rxjs';
import { LoaderService } from '../../../core/components/loader/loader.service';
import { MapBoardService } from '../services/map-board-service';
import { KeyDisplayNamePipe } from './key-display-name.pipe';
import { ActionType, SeverityLevel } from '../../../shared/enums/common';
import { LoaderComponent } from '../../../core/components/loader/loader.component';
import { EnvConfigService } from '../../../shared/services/env-config.service';
import { UserRole } from '../../../core/auth/enums/userRoles';
import { EmergencyAlertComponent } from '../../../shared/components/emergency-alert/emergency-alert.component';
import { StudentsListDialogComponent } from '../../monitoring-center/components/ride-item/students-list-dialog/students-list-dialog.component';
import { DialogService } from 'primeng/dynamicdialog';
import { RidesExtraFields } from '../../../shared/enums/extraFields';

@Component({
  selector: 'app-rides-board-layout',
  standalone: true,
  providers: [MessageService, DialogService],
  imports: [TableModule, ScrollerModule, CommonModule, KeyDisplayNamePipe, TooltipModule, LoaderComponent, EmergencyAlertComponent],
  templateUrl: './rides-board-layout.component.html',
  styleUrl: './rides-board-layout.component.scss',
})
export class RidesBoardLayoutComponent implements OnInit, OnDestroy {
  private readonly stateService = inject(StateService);
  private readonly firestoreService = inject(FireStoreService);
  private readonly env = inject(EnvConfigService);
  private readonly dialogService = inject(DialogService);

  ridesData: RideBoardListItem[] = [];
  pageSize = 40;
  totalRecords = 0;
  latestScrollTop = 0;
  columns: string[] = [];
  viewportHeightInPixels = 0;
  viewportHeight = signal<string>('calc(100dvh - 2rem)'); // padding-y
  calculated100dvh = signal<string>('100dvh');
  handleResizeListener: any;

  severityLevel = signal(SeverityLevel);

  tooltipOptions: TooltipOptions = {
    showDelay: 250,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
    tooltipStyleClass: 'tooltip-translate-20',
  };

  private readonly loaderService = inject(LoaderService);
  private readonly mapBoardService = inject(MapBoardService);
  private readonly router = inject(Router);

  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  userRoles = UserRole;
  loaderState$ = this.loaderService.loaderState;
  FSRidesSubscription!: Subscription;
  totalRecordsSubscription!: Subscription;
  scrollSubscription!: Subscription | null;
  clientSideCurrentPage = 1;
  allItemsLoaded = false;
  ridesMap = new Map();
  readonly primeTable = viewChild<Table>('primeTable');
  readonly mainContainer = viewChild.required<ElementRef>('mainContainer');
  isRunAsLibrary = this.env.getConfig().isRunAsLibrary;
  loaderCount = 0;

  // Custom column resize properties
  private resizingColumnIndex: number | null = null;
  private startX: number = 0;
  private startWidth: number = 0;
  private startTableWidth: number = 0;
  private initialTableWidth: number = 0;
  private readonly zoomFactor = 0.8;

  onResizeStart(event: MouseEvent, columnIndex: number) {
    event.preventDefault();

    const th = (event.target as HTMLElement).closest('th');
    if (!th) return;

    const table = this.primeTable()?.el.nativeElement.querySelector('table');
    if (!table) return;

    this.resizingColumnIndex = columnIndex;
    this.startX = event.clientX;
    this.startWidth = th.offsetWidth;
    this.startTableWidth = table.offsetWidth;

    // Capture initial table width on first resize (100% of container)
    if (this.initialTableWidth === 0) {
      const tableContainer = table.parentElement;
      this.initialTableWidth = tableContainer?.offsetWidth ?? table.offsetWidth;
    }

    // Add listeners
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeEnd);

    // Add visual feedback
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  private readonly onResizeMove = (event: MouseEvent) => {
    if (this.resizingColumnIndex === null) return;

    // Calculate the delta with zoom compensation
    const deltaX = (event.clientX - this.startX) / this.zoomFactor;
    const newWidth = Math.max(50, this.startWidth + deltaX); // Min width 50px
    const widthDifference = newWidth - this.startWidth;

    // Apply to both th and corresponding td cells
    const table = this.primeTable()?.el.nativeElement.querySelector('table');
    if (!table) return;

    // Update header
    const th = table.querySelector(`thead th:nth-child(${this.resizingColumnIndex + 1})`) as HTMLElement;
    if (th) {
      th.style.width = `${newWidth}px`;
      th.style.minWidth = `${newWidth}px`;
    }

    // Update all body cells in this column
    const cells = table.querySelectorAll(`tbody tr td:nth-child(${this.resizingColumnIndex + 1})`);
    cells.forEach((cell: Element) => {
      (cell as HTMLElement).style.width = `${newWidth}px`;
      (cell as HTMLElement).style.minWidth = `${newWidth}px`;
    });

    // Expand the table width by the same amount the column grew
    // But never let it shrink below the initial 100% width
    const newTableWidth = Math.max(this.initialTableWidth, this.startTableWidth + widthDifference);
    table.style.width = `${newTableWidth}px`;
    table.style.minWidth = `${newTableWidth}px`;
  };

  private readonly onResizeEnd = () => {
    this.resizingColumnIndex = null;

    // Remove listeners
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);

    // Remove visual feedback
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  ngOnInit() {
    this.notifyFullscreenState(true);
    this.getRidesAfterGettingTotalCount();
    this.totalRecordsSubscription = this.firestoreService.totalRides$.subscribe(res => {
      this.totalRecords = res;
      console.log('Total records: ', this.totalRecords);
    });

    this.updateHeight();
    this.setupResizeListener();
  }

  setupScrollListener() {
    // Clean up existing subscription if any
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
      this.scrollSubscription = null;
    }

    // Get the scrollable element from PrimeNG's virtualScroller
    const scrollableElement = this.primeTable()?.el?.nativeElement.querySelector('.p-datatable-wrapper');

    if (!scrollableElement) {
      console.error('Could not find scrollable element');
      return;
    }

    // Add scroll event listener
    this.scrollSubscription = fromEvent(scrollableElement, 'scroll')
      .pipe(debounceTime(200)) // Prevent too many events
      .subscribe(e => {
        this.checkScrollPosition(scrollableElement);
      });

    console.log('Scroll listener attached');
  }

  openStudentsListDialog(studentNames: string[]) {
    this.dialogService.open(StudentsListDialogComponent, {
      showHeader: false,
      width: '640px',
      data: {
        studentNames,
      },
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
    });
  }

  checkScrollPosition(element: HTMLElement) {
    // Don't trigger if we're already loading or all items are loaded
    if (this.allItemsLoaded) {
      return;
    }

    // Calculate scroll position
    const scrollPosition = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Calculate how close to the bottom we are (as a percentage)
    const scrollPercentage = (scrollPosition + clientHeight) / scrollHeight;

    // If we're close to the bottom (e.g., within 20% of the total height)
    if (scrollPercentage > 0.85) {
      this.loadMore();
    }
  }

  notifyFullscreenState(isFullscreen: boolean): void {
    if (window.parent) {
      window.parent.postMessage(
        {
          action: isFullscreen ? 'enterFullscreen' : 'exitFullscreen',
          source: 'angular-iframe',
        },
        this.env.getConfig().adrAdminUrl,
      );
    }
  }

  trackByRideId(index: number, ride: any): number {
    return ride.rideId;
  }

  navigateToMC() {
    if (this.env.getConfig().isRunAsLibrary) {
      this.router.navigateByUrl(`/dispatch/monitoring-center`);
    } else {
      this.router.navigateByUrl(`/monitoring-center`);
    }
  }

  private setupResizeListener() {
    if (globalThis) {
      const handleResize = () => {
        this.updateHeight();
      };
      this.handleResizeListener = handleResize;
      globalThis.addEventListener('resize', handleResize);
    }
  }

  private updateHeight() {
    if (globalThis) {
      let zoomFactor = 0.8; // Assume 80% zoom by default as is the current requirement
      const mainContainer = this.mainContainer();
      if (mainContainer?.nativeElement) {
        const computedStyle = globalThis.getComputedStyle(mainContainer.nativeElement);
        const zoomValue = computedStyle.zoom;

        if (zoomValue !== 'normal') {
          zoomFactor = Number.parseFloat(zoomValue);
        }
      }
      const containerHeight = globalThis.innerHeight / zoomFactor;

      const newHeight = `calc(${containerHeight}px - 2rem)`;
      this.viewportHeight.set(newHeight);
      this.calculated100dvh.set(`${containerHeight - 1}px`);
    }
  }

  loadMore() {
    if (this.allItemsLoaded || this.ridesData.length >= this.totalRecords || this.loaderCount > 0) {
      return;
    }

    const scroller = this.getScrollableElement();
    this.latestScrollTop = scroller?.scrollTop ?? 0;
    console.log(`Stored scroll top: ${this.latestScrollTop}`);
    this.getRidesAfterGettingTotalCount();
  }

  async getRidesAfterGettingTotalCount() {
    if (!this.stateService.getFirebaseUser()) return;
    let queryConstraints: QueryConstraint[] = [
      where('index_date_of_service', '==', moment().format('L')), // MM/DD/YYYY
      where('ride_status_id', '!=', RideStatusType.Deleted),
      orderBy('ride_order'),
    ];

    const currentRole = (localStorage.getItem('role') ?? '')?.toLowerCase();
    const consumerIdentity = localStorage.getItem('consumer_identity') ?? null;
    if (currentRole == UserRole.Client && consumerIdentity) {
      queryConstraints.push(where('index_client_ids', 'array-contains', Number(consumerIdentity)));
    } else if (currentRole == UserRole.Provider && consumerIdentity) {
      queryConstraints.push(where('index_organization_id', '==', Number(consumerIdentity)));
    }

    this.showLoader();
    await this.firestoreService.getTotalRidesCount(this.env.getConfig().ridesCollection, queryConstraints);
    this.listenOnRideUpdates(queryConstraints);
  }

  listenOnRideUpdates(queryConstraints: QueryConstraint[]) {
    if (this.FSRidesSubscription) {
      this.FSRidesSubscription.unsubscribe();
    }

    let totalItemsFetched = { value: 0 }; // Track total items fetched including test rides (using object for pass-by-reference)
    let mappedRides: RideBoardListItem[] = [];
    let batch = this.clientSideCurrentPage * 40;

    const setPageData = () => {
      mappedRides.sort((a, b) => a.rideOrder - b.rideOrder);
      this.setRides(mappedRides, true);
      this.allItemsLoaded = this.areAllItemsLoaded();
    };

    this.FSRidesSubscription = this.firestoreService.getRidesData(this.env.getConfig().ridesCollection, batch, queryConstraints).subscribe({
      next: (res: any) => {
        console.log(res);

        if (res.newItems.length > 0) {
          this.processNewItems(res.newItems, mappedRides, totalItemsFetched, setPageData);
        } else {
          this.hideLoader();
        }

        if (res.changes.length > 0) {
          this.processRideChanges(res.changes, mappedRides);
        }
      },
      error: err => {
        console.log(err);
        this.hideLoader();
      },
    });
  }

  private processNewItems(newItems: RideDocumentDto[], mappedRides: RideBoardListItem[], totalItemsFetched: { value: number }, setPageData: () => void) {
    let newItemsNumber = 0;

    newItems.forEach((rideDocument: RideDocumentDto) => {
      const isTestRide = rideDocument.ride_extra_fields?.find((field: RideExtraFieldDocumentDto) => field.key_id == RidesExtraFields.IsTest)?.value;
      totalItemsFetched.value += 1;
      if (isTestRide?.toLowerCase() === 'true') {
        console.log('test ride - skipping');
      } else {
        newItemsNumber += 1;
        const mappedRide = this.mapBoardService.getMappedItem(rideDocument);
        mappedRides.push(mappedRide);
        this.ridesMap.set(rideDocument.original_ride_id, mappedRide);
      }
    });

    this.handlePagination(totalItemsFetched.value, mappedRides, setPageData);
    console.log(mappedRides);
  }

  private handlePagination(totalItemsFetched: number, mappedRides: RideBoardListItem[], setPageData: () => void) {
    const lastPage = Math.ceil(this.totalRecords / this.pageSize);
    console.log(`Last Page is `, lastPage);

    if (this.clientSideCurrentPage != lastPage) {
      console.log('not last page');
      console.log(`Current page ${this.clientSideCurrentPage} Items fetched ${totalItemsFetched} should be ${this.pageSize * this.clientSideCurrentPage}`);
      if (totalItemsFetched >= this.pageSize * this.clientSideCurrentPage) {
        console.log(`total items fetched >= ${this.pageSize * this.clientSideCurrentPage} ✔ setting rides & incrementing current page`);
        setPageData();
        this.clientSideCurrentPage += 1;

        // If all rides were test rides, automatically fetch next page
        if (mappedRides.length === 0 && !this.allItemsLoaded) {
          console.log('All rides were test rides, fetching next page...');
          this.getRidesAfterGettingTotalCount();
        }
      }
    } else {
      console.log(`Last page`);
      console.log(`Items fetched ${totalItemsFetched} should equal total records: ${this.totalRecords}`);
      if (totalItemsFetched >= this.totalRecords) {
        console.log(`total items fetched = ${this.totalRecords} ✔ setting rides`);
        setPageData();
      }
    }
  }

  private processRideChanges(changes: { id: string; data: RideDocumentDto; type: string }[], mappedRides: RideBoardListItem[]) {
    changes.forEach(c => {
      if (c.type == ActionType.modified) {
        this.handleModifiedRide(c.data, mappedRides);
      }
      if (c.type == ActionType.removed) {
        this.handleRemovedRide(c.data, mappedRides);
      }
    });
  }

  private handleModifiedRide(data: RideDocumentDto, mappedRides: RideBoardListItem[]) {
    const mappedRide: RideBoardListItem = this.mapBoardService.getMappedItem(data);
    const displayedRide: RideBoardListItem | undefined = this.ridesData.find(r => r.rideId == data.original_ride_id);

    const mappedRideIndex = mappedRides.findIndex(r => r.rideId == data.original_ride_id);
    mappedRides.splice(mappedRideIndex, 1, mappedRide);

    if (displayedRide) {
      const rideIndex = this.ridesData.findIndex(r => r.rideId == displayedRide.rideId);
      this.updateRideItem(displayedRide, mappedRide, rideIndex);
      if (displayedRide.status != mappedRide.status) {
        this.updateStatus(displayedRide, rideIndex);
      }
      if (displayedRide.status == mappedRide.status && displayedRide.rideOrder != mappedRide.rideOrder) {
        this.updateRideOrder(displayedRide);
      }
    }
  }

  private handleRemovedRide(data: RideDocumentDto, mappedRides: RideBoardListItem[]) {
    const rideIndex = mappedRides.findIndex(r => r.rideId == data.original_ride_id);
    mappedRides.splice(rideIndex, 1);
    mappedRides.sort((a, b) => a.rideOrder - b.rideOrder);
    this.setRides(mappedRides, false);
  }

  updateRideItem(displayedRide: RideBoardListItem, newRide: RideBoardListItem, rideIndex: number) {
    const previousIndex = displayedRide.previousIndex;

    newRide.previousIndex = previousIndex;
    newRide.currentIndex = rideIndex;
    this.ridesData[rideIndex] = newRide;
  }

  // Calculate the transform based on position change
  getRowTransform(ride: RideBoardListItem): string {
    if (ride.previousIndex === undefined || ride.currentIndex === undefined || ride.previousIndex === ride.currentIndex) {
      return 'translateY(0)';
    }

    const diff = ride.previousIndex - ride.currentIndex;
    const distance = diff * 60; // IMPORTANT row height is 60

    return `translateY(${distance}px)`;
  }

  updateRideOrder(updatedRide: RideBoardListItem) {
    // Sorting a copy of the rides array
    // This changes the order of the object references within sortedItems, but it does not affect the order of objects in the original ridesData array.
    // But the sortedItems array still holds references to the original objects
    const sortedItems = [...this.ridesData].sort((a, b) => a.rideOrder - b.rideOrder);

    // Update current indices based on new positions
    sortedItems.forEach((item, newIndex) => {
      item.currentIndex = newIndex; // (will also update the currentIndex in the rideArray since it references the same object)
    });

    this.handleAnimations(sortedItems, updatedRide);
  }

  async handleAnimations(sortedItems: any[], updatedRide: any): Promise<void> {
    // Step 1: re-sort, keeping each row visually at its old position (no transition for this frame)
    await this.delay(1000);
    sortedItems.forEach(item => {
      item.transform = this.getRowTransform(item);
      item.skipTransition = true;
    });
    this.ridesData = sortedItems;

    // Step 2: slide rows to their new position via the row's CSS transform transition
    await this.delay(10);
    this.updateRidesTransform();

    // Step 3: Highlight updated row if needed
    if (updatedRide) {
      await this.delay(1000);
      this.highlightUpdatedRide(updatedRide);
    }
  }

  private showLoader() {
    this.loaderService.show();
    this.loaderCount++;
  }
  private hideLoader() {
    while (this.loaderCount > 0) {
      this.loaderService.hide();
      this.loaderCount--;
    }
  }

  private updateRidesTransform(): void {
    this.ridesData.forEach(ride => {
      ride.previousIndex = ride.currentIndex;
      ride.transform = this.getRowTransform(ride);
      ride.skipTransition = false;
    });
  }

  private async highlightUpdatedRide(updatedRide: any): Promise<void> {
    const index = this.ridesData.findIndex(ride => ride.rideId === updatedRide.rideId);
    if (index !== -1) this.ridesData[index]['alert'] = true;

    // remove alert after animation duration
    await this.delay(5000);
    const newIndex = this.ridesData.findIndex(ride => ride.rideId === updatedRide.rideId); // get the index again to make sure it is the correct index the timeout
    if (newIndex !== -1) this.ridesData[newIndex]['alert'] = false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  updateStatus(ride: RideBoardListItem, rideIndex: number) {
    this.ridesData[rideIndex]['statusUpdated'] = true;

    setTimeout(() => {
      const rideIndex = this.ridesData.findIndex(r => r.rideId == ride.rideId); // making sure to access the right ride index if the index was changed during the timeout
      this.ridesData[rideIndex].statusUpdated = false;
      this.updateRideOrder(ride);
    }, 800); // after the status change animation
  }

  private areAllItemsLoaded(): boolean {
    if (this.clientSideCurrentPage >= this.totalRecords / this.pageSize) {
      return true;
    }

    return false;
  }

  private async setRides(rides: RideBoardListItem[], restoreScrollPosition: boolean) {
    this.ridesData = rides;
    if (this.ridesData.length > 0)
      setTimeout(() => {
        this.setupScrollListener();
      }, 500);

    this.ridesData.forEach((ride, index) => {
      ride.currentIndex = index;
      ride.previousIndex = index;
    });

    if (this.ridesData.length > 0) {
      this.columns = Object.keys(this.ridesData[0]).filter(key => {
        const alwaysExcluded = ['statusUpdated', 'alert', 'rideOrder', 'rideAlertColor', 'statusSeverity', 'previousIndex', 'currentIndex', 'transform', 'rideId'];

        // Conditionally filter out providerName for client users
        const conditionallyExcluded = this.currentRole == UserRole.Client ? ['providerName', 'driverName'] : [];
        const excludedKeys = [...alwaysExcluded, ...conditionallyExcluded];

        return !excludedKeys.includes(key);
      });
    }

    if (!restoreScrollPosition) {
      this.hideLoader();
      return;
    }

    await this.delay(100);
    const newScroller = this.getScrollableElement();
    if (newScroller) {
      // Check if scroller still exists
      // Only restore if we had a meaningful position before
      if (this.latestScrollTop > 0) {
        await this.delay(250);
        newScroller.scrollTop = this.latestScrollTop;

        await this.delay(250);
        this.hideLoader();
      } else {
        this.hideLoader();
      }
    } else {
      console.warn('Scroller element not found after update.');
      this.hideLoader();
    }
  }

  // --- Helper to get the scrollable body element ---
  private getScrollableElement(): HTMLElement | null {
    const primeTable = this.primeTable();
    if (primeTable?.el?.nativeElement) {
      return primeTable.el.nativeElement.querySelector('.p-datatable-wrapper');
    }
    console.warn('p-table nativeElement not found.');
    return null;
  }

  ngOnDestroy() {
    this.notifyFullscreenState(false);
    this.FSRidesSubscription?.unsubscribe();
    this.totalRecordsSubscription?.unsubscribe();
    window.removeEventListener('resize', this.handleResizeListener);
    this.firestoreService.setTotalRidesCount(0);
  }
}
